// src/auth/useCustomAuth.ts

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
//import { useMutation } from '@tanstack/react-query';
import { CUSTOM_METHOD, storage, validateCognitoToken } from './utils';
import { REFRESH_ENDPOINT } from './config';
import { useLazyRenewSmsQuery, useLazyLogoutSMSQuery, VerifyResult } from '../../features/oidcSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { clearSessionRTK, CustomSession, selectAuthMethodRTK, selectCustomSessionRTK, selectSessionRTK, setAuthenticated, setAuthReady, setSessionForRTK } from '../../features/userTokenManagementSlice';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { printError } from '../utils';
const REFRESH_THRESHOLD_MS = 60000; // Renew 60 seconds before token expires
export interface useCustomAuthInterface { // Standardized fields
    isAuthenticated: boolean,
    user: any,
    isLoading: boolean,
    // Custom flow methods
    login: (responseData: VerifyResult/*, navigateTo?: string*/) => void,
    logout: () => Promise<void>,
    renewIfExpired: (/*callBackEndLogoutOnFailure?: boolean*/) => Promise<{success: boolean, error?: string,  session?: CustomSession}> //not used since we are using reactive renewal
    // Raw session data (use sparingly)
    session: CustomSession | null | undefined,
    error: any,
    updateSessionPhone: (newPhone: string) => boolean,
    clearLocalAndRTKSesstion: () => void
    refreshMutation: () => Promise<CustomSession | undefined>
    isSuccessRenew: boolean
    isSuccessLogout: boolean
    isExpired: () => boolean | undefined

}
export const useCustomAuth = (): useCustomAuthInterface => {
    const dispatch = useAppDispatch();
    //const navigate = useNavigate();
    const authMethod = useSelector(selectAuthMethodRTK)
    const session = useSelector(selectCustomSessionRTK) 
    const [error, setError] = useState<string | null>();
    // const [ renew, { data: startData, isLoading: starting, isSuccess: startSuccess, error: startError }] = useRenewSmsMutation();
    const [triggerRenewSms, { data, isLoading: isLoadingRenew, isSuccess: isSuccessRenew, error: renewError }] = useLazyRenewSmsQuery();
    const [triggerLogoutSms, { isLoading: isLoadingLogout, isSuccess: isSuccessLogout, error: logoutError }] = useLazyLogoutSMSQuery();

    const refreshMutation = async () => {
        // --- 1. Renewal Function (Pillar) ---
        try {
            const response = await triggerRenewSms('bootstrap').unwrap() //clears tokens on faiulure
            const newSession = { expires_at: response.expires_at, sub: response.profile.sub, profile: response.profile } as CustomSession;
            dispatch(setSessionForRTK({ newSession, authMethod: CUSTOM_METHOD }));
            storage.saveSession(newSession);
            return newSession
            //},
        } catch (err) {
            //onError: () => {
            console.log('  Failure means the refresh token is dead -> force logout')
            //triggerRenewSms() clears tokens on faiulure
            clearLocalAndRTKSesstion() //logout( )//logout();
        }

    };
    const updateSessionPhone = (newPhone: string) => {
        if (!session || !session?.profile) {
            console.error(`invalid session while updating custom auth session with ${newPhone}`)
            return false
        }
        const updatedProfile = { ...session?.profile }
        updatedProfile.phone_number = newPhone
        const updatedSession = { ...session, profile: updatedProfile }
        dispatch(setSessionForRTK({ newSession: updatedSession, authMethod: CUSTOM_METHOD }));
        storage.saveSession(updatedSession);
        return true
    }


    // --- 2. Login Function ---
    const login = useCallback(async (responseData: any /*, navigateTo: string = "/"*/) => {
        try {
            const newSession: CustomSession = {
                expires_at: responseData.expires_at, // Provided by your backend
                sub: responseData.profile.sub,
                profile: responseData.profile,
            };

            dispatch(setSessionForRTK({ newSession, authMethod: CUSTOM_METHOD }));
            storage.saveSession(newSession);
            //test

            //     // navigate to a secure page
            //    //if (navigateTo) {
            //         navigate(navigateTo || "/");//('/');
            //     //}

        } catch (error) {
            console.error('Login validation failed:', error);
            // Handle error, maybe show a message
            setError(`Login validation failed: ${error}`)
        }
    }, [])//navigate]);

    const clearLocalAndRTKSesstion = () => {
        storage.clearSession();
        dispatch(clearSessionRTK());
        dispatch(setAuthenticated(false));
    }
    // --- 3. Robust Logout ---
    const logout = useCallback(async () => {//callLogoutSms: boolean = true/*navigateTo: string = ''*/) => { // <-- FIX 1: Add 'async' here
        try {
            // 1. Attempt to call the backend revocation endpoint (to clear HttpOnly cookie/server session)
            // Using 'await' ensures we wait for the server's response (or failure).
            //if( callLogoutSms) {
            await triggerLogoutSms().unwrap();
            //}
            console.log('Logout API call successful.');
        } catch (err) {
            // 2. Catch the error, but proceed with client cleanup.
            // The API call often fails (e.g., if the Refresh Token is already gone/expired), 
            // but we must still clear client state.
            console.error('Failure in logout backend call, proceeding with client cleanup:', err);
        }

        // 3. Client-side cleanup (MUST run regardless of API success/failure)
        clearLocalAndRTKSesstion()
        // if (navigateTo)
        //     navigate('navigateTo');
    }, [/*navigate,*/ triggerLogoutSms, clearLocalAndRTKSesstion])//storage, clearSessionRTK, setAuthenticated]); // <-- FIX 2: Ensure trigger is in dependencies

    // using reactive renewal, sowe dont need to do the same in a timer
    // // --- 4. Auto-Renewal Timer and Persistence (Housekeeping) ---
    // useEffect(() => {
    //     let timer: NodeJS.Timeout | null = null;

    //     // if (session ) {
    //     //     const now = Date.now() / 1000; // Current time in seconds
    //     //     const expirySec = session?.expires_at;
    //     //     const timeToExpiryMs = Math.max(0, (expirySec - now) * 1000);

    //     //     if (timeToExpiryMs < REFRESH_THRESHOLD_MS) {
    //     //         // Token is nearing expiry, renew immediately
    //     //         console.log('Access token expiring soon. Triggering renewal...');
    //     //         refreshMutation() //refersh token is the http tocken?session.refresh_token);//mutate(session.refresh_token);
    //     //     }

    //     //     const renewInMs = timeToExpiryMs - REFRESH_THRESHOLD_MS;

    //     //     // Set a timer to trigger the renewal
    //     //     timer = setTimeout(() => {
    //     //         if (session.refresh_token) {
    //     //             refreshMutation()//.mutate(session.refresh_token);
    //     //         } else {
    //     //             forceLogout();
    //     //         }
    //     //     }, Math.max(0, renewInMs));
    //     // }

    //     // // Cleanup the timer when component unmounts or session changes
    //     // return () => {
    //     //     if (timer) clearTimeout(timer);
    //     // };
    // }, [session, refreshMutation/*.mutate*/, forceLogout]);

    // --- 5. Pre-Request Check Utility ---
    const isExpired = () => {
        if (!session) return undefined;

        // Convert expiry to milliseconds for comparison
        const expiresAtMs = session?.expires_at ??  1000

        if (expiresAtMs > ((Date.now()/1000) + 5)) { // Still valid for 5 seconds
            return false //session.access_token;
        }
        return true
    }

    const renewIfExpired = useCallback(async (): Promise<{success: boolean, error?: string,  session?: CustomSession}> => { //returns true if success, does not throw exception
        if (!session) return {success:false, error: "session unavalable"};

        // Convert expiry to milliseconds for comparison
        const expiresAtMs = session?.expires_at ?? 0 * 1000

        if (expiresAtMs > Date.now() + 5000) { // Still valid for 5 seconds
            return {success: true} //session.access_token;
        }

        console.log('Access token expired or nearing expiry. Attempting synchronous renewal...');
        try {
            // Block execution and wait for a new token
            const newSession = await refreshMutation()//.mutateAsync(session.refresh_token);
            return {success:true, session: newSession}//newSession?.access_token ?? null;
        } catch (e) {
            console.error('Failed to get new token:', e);
            //clearLocalAndRTKSesstion() //logout( )
            return  {success:false, error: printError(e)}//null;
        }

    }, [session, refreshMutation/*.mutateAsync*/, logout]);


    // if( session?.profile){
    //     session.profile .expired = isExpired()
    // }
    const isAuthenticated = !!session && !isExpired()
    return useMemo(() => ({
        // Standardized fields
        isAuthenticated,
        user: session ? session.profile : null,

        // Custom flow methods
        login,
        logout,
        renewIfExpired,
        isLoading: isLoadingRenew,
        // Raw session data (use sparingly)
        session,
        error: renewError || error,
        updateSessionPhone,
        clearLocalAndRTKSesstion: clearLocalAndRTKSesstion,
        refreshMutation, isExpired,
        isSuccessRenew, isSuccessLogout
    }), [session, login, logout, renewIfExpired, isLoadingRenew, updateSessionPhone]);
};